import React from 'react';
import { useGlobalStore } from '../../../application/store/store';
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    TextField,
    Typography,
} from '@mui/material';

const DISPOSAL_REASONS = {
    expired: 'Utgånget datum',
    broken: 'Kross',
    temperatureExposed: 'Utsatt för värme/kyla',
    other: 'Annat',
} as const;

const DisposedCause: React.FC = () => {
    const disposedCause = useGlobalStore((state) => state.disposedCause);
    const setDisposedCause = useGlobalStore((state) => state.setDisposedCause);

    const handleChange =
        (reason: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            let newReasons = [...disposedCause.reasons];

            if (event.target.checked) {
                if (!newReasons.includes(reason)) {
                    newReasons.push(reason);
                }
            } else {
                newReasons = newReasons.filter((r) => r !== reason);
                // If unchecking "Annat", remove any custom comment
                if (reason === DISPOSAL_REASONS.other) {
                    newReasons = newReasons.filter((r) =>
                        Object.values(DISPOSAL_REASONS).includes(r as any)
                    );
                }
            }

            setDisposedCause(newReasons);
        };

    const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const noteText = event.target.value;
        // Remove any previous custom comments (non-standard reasons)
        let newReasons = disposedCause.reasons.filter((r) =>
            Object.values(DISPOSAL_REASONS).includes(r as any)
        );

        if (noteText.trim()) {
            newReasons.push(`${DISPOSAL_REASONS.other}: ${noteText}`);
        }

        setDisposedCause(newReasons);
    };

    const getCustomComment = () => {
        const customComment =
            disposedCause.reasons.find(
                (r) => !Object.values(DISPOSAL_REASONS).includes(r as any)
            ) || '';

        return customComment.startsWith(`${DISPOSAL_REASONS.other}: `)
            ? customComment.substring(DISPOSAL_REASONS.other.length + 2)
            : customComment;
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 500,
                mx: 'auto',
                p: 3,
                backgroundColor: 'background.paper',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
            }}
        >
            <Typography
                variant="h6"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'primary.main',
                    borderBottom: '2px solid',
                    borderColor: 'secondary.main',
                    paddingBottom: 1,
                }}
            >
                Orsak till kassering
            </Typography>
            <FormGroup sx={{ ml: 1 }}>
                {Object.values(DISPOSAL_REASONS).map((reason) => (
                    <FormControlLabel
                        key={reason}
                        control={
                            <Checkbox
                                checked={disposedCause.reasons.includes(reason)}
                                onChange={handleChange(reason)}
                                color="primary"
                                sx={{
                                    '&.Mui-checked': {
                                        color: '#0B4F76',
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ color: 'text.primary' }}>
                                {reason}
                            </Typography>
                        }
                        sx={{ mb: 1.5 }}
                    />
                ))}
                {disposedCause.reasons.includes(DISPOSAL_REASONS.other) && (
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        label="Kommentar"
                        value={getCustomComment()}
                        onChange={handleNoteChange}
                        variant="outlined"
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                    />
                )}
            </FormGroup>
        </Box>
    );
};

export default DisposedCause;
